import React, { useState } from "react";
import { FormControl, InputLabel, TextField } from "@mui/material";
import { useNavigate } from "react-router-dom";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import { useCalendlyEventListener, InlineWidget } from "react-calendly";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import MDSnackbar from "components/MDSnackbar";

// Material Dashboard 2 React example components
import PageLayout from "examples/LayoutContainers/PageLayout";
import Footer from "examples/Footer";

// ENV Values
const token = process.env.REACT_APP_BEARER_TOKEN;
const apiUrl = process.env.REACT_APP_AUTH_API_URL;

function PublicEventRequest() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [eventId, setEventId] = useState(0);
  const [formData, setFormData] = useState({
    location: "",
    addresses: [
      { city: "", province: "", street: "", houseNumber: "", postalCode: "", locationName: "" },
    ],
    startDates: [""],
    endDates: [""],
    eventType: [],
    permissions: [],
    eventName: "",
    additionalDetails: "",
    firstName: "",
    lastName: "",
    phoneNumber: "",
    email: "",
    companyName: "",
  });

  const [validationErrors, setValidationErrors] = useState({
    addresses: [
      {
        city: false,
        province: false,
        street: false,
        houseNumber: false,
        postalCode: false,
        locationName: false,
      },
    ],
    startDates: [false],
    endDates: [false],
    eventName: false,
    email: false,
  });

  const [snackbar, setSnackbar] = useState({
    open: false,
    color: "",
    icon: "",
    title: "",
    message: "",
  });

  const steps = [
    "Seleziona la Posizione",
    "Inserisci l'Indirizzo",
    "Imposta le Date dell'Evento",
    "Seleziona il Tipo di Evento",
    "Seleziona i Permessi",
    "Inserisci il Nome dell'Evento",
    "Dettagli Aggiuntivi",
    "Inserisci le Informazioni Personali",
    "Per proseguire fissa un appuntamento con un nostro esperto",
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleOptionClick = (field, value) => {
    setFormData({ ...formData, [field]: value });
    setStep((prevStep) => prevStep + 1);
  };

  const handleMultiSelectClick = (field, option) => {
    let currentSelection = [...formData[field]];
    if (currentSelection.includes(option)) {
      currentSelection = currentSelection.filter((item) => item !== option);
    } else {
      currentSelection.push(option);
    }
    setFormData({ ...formData, [field]: currentSelection });
  };

  const addAddressField = () => {
    const newAddresses = [
      ...formData.addresses,
      { city: "", province: "", street: "", houseNumber: "", postalCode: "", locationName: "" },
    ];
    const newStartDates = [...formData.startDates, ""];
    const newEndDates = [...formData.endDates, ""];
    setFormData({
      ...formData,
      addresses: newAddresses,
      startDates: newStartDates,
      endDates: newEndDates,
    });
    setValidationErrors({
      ...validationErrors,
      addresses: [
        ...validationErrors.addresses,
        {
          city: false,
          province: false,
          street: false,
          houseNumber: false,
          postalCode: false,
          locationName: false,
        },
      ],
      startDates: [...validationErrors.startDates, false],
      endDates: [...validationErrors.endDates, false],
    });
  };

  const removeAddressField = () => {
    const newAddresses = formData.addresses.slice(0, -1);
    const newStartDates = formData.startDates.slice(0, -1);
    const newEndDates = formData.endDates.slice(0, -1);
    setFormData({
      ...formData,
      addresses: newAddresses,
      startDates: newStartDates,
      endDates: newEndDates,
    });
    setValidationErrors({
      ...validationErrors,
      addresses: validationErrors.addresses.slice(0, -1),
      startDates: validationErrors.startDates.slice(0, -1),
      endDates: validationErrors.endDates.slice(0, -1),
    });
  };

  const handleAddressChange = (index, field, value) => {
    const newAddresses = [...formData.addresses];
    newAddresses[index][field] = value;
    setFormData({ ...formData, addresses: newAddresses });

    const newValidationErrors = { ...validationErrors };
    newValidationErrors.addresses[index][field] = !value;
    setValidationErrors(newValidationErrors);
  };

  const validateAdress = () => {
    let isValid = true;
    const newValidationErrors = { addresses: [] };

    formData.addresses.forEach((address, index) => {
      const addressErrors = {
        city: !address.city,
        province: !address.province,
        street: !address.street,
        houseNumber: !address.houseNumber,
        postalCode: !address.postalCode,
        locationName: !address.locationName,
      };
      newValidationErrors.addresses[index] = addressErrors;
      if (Object.values(addressErrors).some((error) => error)) {
        isValid = false;
      }
    });

    setValidationErrors({ ...validationErrors, ...newValidationErrors });
    return isValid;
  };

  const validateDates = () => {
    const now = new Date().toISOString().split("T")[0];
    let isValid = true;
    const newValidationErrors = { startDates: [], endDates: [] };

    formData.startDates.forEach((startDate, index) => {
      if (startDate < now) {
        isValid = false;
        newValidationErrors.startDates[index] = true;
        setSnackbar({
          open: true,
          message: "La data di inizio non può essere precedente alla data attuale.",
          severity: "error",
        });
      } else {
        newValidationErrors.startDates[index] = false;
      }
    });

    formData.endDates.forEach((endDate, index) => {
      if (endDate < formData.startDates[index]) {
        isValid = false;
        newValidationErrors.endDates[index] = true;
        setSnackbar({
          open: true,
          message: "La data di fine non può essere precedente alla data di inizio.",
          severity: "error",
        });
      } else {
        newValidationErrors.endDates[index] = false;
      }
    });

    setValidationErrors({ ...validationErrors, ...newValidationErrors });
    return isValid;
  };

  const handleDateChange = (index, field, value) => {
    const newDates = [...formData[field]];
    newDates[index] = value;
    setFormData({ ...formData, [field]: newDates });

    if (field === "startDates" || field === "endDates") {
      const newValidationErrors = { ...validationErrors };
      newValidationErrors[field][index] = !value;
      setValidationErrors(newValidationErrors);
    }
  };

  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const handleNext = () => {
    if (step === 1) {
      if (!validateAdress()) {
        setSnackbar({
          open: true,
          color: "error",
          icon: "error",
          title: "Informazioni Mancanti",
          message: "Si prega di compilare tutti i campi dell'indirizzo.",
        });
        return;
      }
    }
    if (step === 2) {
      if (!validateDates()) {
        setSnackbar({
          open: true,
          color: "error",
          icon: "error",
          title: "Date Mancanti",
          message: "Si prega di inserire tutte le date richieste.",
        });
        return;
      }
    }
    if (step === 3 && formData.eventType.length === 0) {
      setSnackbar({
        open: true,
        color: "error",
        icon: "error",
        title: "Seleziona almeno un'opzione.",
        severity: "error",
      });
      return;
    }
    if (step === 4 && formData.permissions.length === 0) {
      setSnackbar({
        open: true,
        color: "error",
        icon: "error",
        title: "Seleziona almeno un'opzione.",
        severity: "error",
      });
      return;
    }
    if (step === 5) {
      if (!formData.eventName) {
        setValidationErrors({ ...validationErrors, eventName: true });
        setSnackbar({
          open: true,
          color: "error",
          icon: "error",
          title: "Il nome dell'evento è obbligatorio.",
          severity: "error",
        });
        return;
      }
    }
    if (step === 7) {
      if (!formData.firstName || !formData.lastName || !formData.phoneNumber || !formData.email) {
        setSnackbar({
          open: true,
          color: "error",
          icon: "error",
          title: "Campi Obbligatori",
          message: "Si prega di compilare tutti i campi obbligatori.",
        });
        return;
      }
      if (!validateEmail(formData.email)) {
        setValidationErrors({ ...validationErrors, email: true });
        setSnackbar({
          open: true,
          color: "error",
          icon: "error",
          title: "Email non valida",
          message: "Inserisci un indirizzo email valido.",
        });
        return;
      }
    }
    setStep((prevStep) => prevStep + 1);
  };

  const handleBack = () => {
    setStep((prevStep) => prevStep - 1);
  };

  const handleSubmit = async () => {
    try {
      formData.addresses.forEach(async (address, index) => {
        const updatedFormData = {
          ...formData,
          addresses: [address],
          startDates: [formData.startDates[index]],
          endDates: [formData.endDates[index]],
        };
        const response = await fetch(`${apiUrl}/public_event_request.php`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(updatedFormData),
        });

        if (response.ok) {
          const data = await response.json();
          if (data.eventId) {
            setEventId(data.eventId);
          }
          setSnackbar({
            open: true,
            color: "success",
            icon: "check_circle",
            title: "Successo",
            message: "La tua richiesta è stata inviata con successo!",
          });
        } else {
          setSnackbar({
            open: true,
            color: "error",
            icon: "error",
            title: "Errore",
            message: "Si è verificato un errore durante l'invio della richiesta.",
          });
        }
      });
      // Redirect to a thank you page or home
      setTimeout(() => {
        navigate("/authentication/sign-in");
      }, 3000);
    } catch (error) {
      console.error("Failed to submit form", error);
      setSnackbar({
        open: true,
        color: "error",
        icon: "error",
        title: "Errore",
        message: "Si è verificato un errore durante l'invio della richiesta.",
      });
    }
  };

  const closeSB = () => setSnackbar({ ...snackbar, open: false });

  const fetchCalendlyDetails = async (eventUri) => {
    try {
      const response = await fetch(eventUri, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = await response.json();
      const startTime = data.resource.start_time;
      await updateNextCallDate(startTime);
    } catch (error) {
      console.error("Error fetching event details:", error);
    }
  };

  const updateNextCallDate = async (startTime) => {
    try {
      const response = await fetch(`${apiUrl}/calendar/update_next_call_date.php`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email: formData.email, startTime, eventId }),
      });

      const result = await response.json();
      if (response.ok) {
        console.log("Successfully updated next call date:", result);
      } else {
        console.error("Error updating next call date:", result);
      }
    } catch (error) {
      console.error("Error updating next call date:", error);
    }
  };

  useCalendlyEventListener({
    onEventScheduled: async (e) => {
      const eventUri = e.data.payload.event.uri;
      handleSubmit();
      fetchCalendlyDetails(eventUri);
    },
  });

  const renderStepContent = () => {
    switch (step) {
      case 0:
        return (
          <Grid container spacing={3} justifyContent="center">
            <Grid item xs={12} lg={8}>
              <Card>
                <MDBox p={2}>
                  <MDTypography variant="h6">
                    Sai già dove si svolgerà il tuo evento? *
                  </MDTypography>
                </MDBox>
                <MDBox pt={2} px={2} pb={2} display="flex" flexDirection="column" gap={2}>
                  <MDButton
                    fullWidth
                    color="secondary"
                    variant="gradient"
                    onClick={() => handleOptionClick("location", "In una location privata")}
                  >
                    In una location privata
                  </MDButton>
                  <MDButton
                    fullWidth
                    color="secondary"
                    variant="gradient"
                    onClick={() => handleOptionClick("location", "Evento su area pubblica")}
                  >
                    Evento su area pubblica
                  </MDButton>
                  <MDButton
                    fullWidth
                    color="secondary"
                    variant="gradient"
                    onClick={() =>
                      handleOptionClick("location", "Una parte privata e una parte pubblica")
                    }
                  >
                    Una parte privata e una parte pubblica
                  </MDButton>
                  <MDButton
                    fullWidth
                    color="secondary"
                    variant="gradient"
                    onClick={() =>
                      handleOptionClick("location", "Non ho ancora deciso la location")
                    }
                  >
                    Non ho ancora deciso la location
                  </MDButton>
                  <MDButton
                    fullWidth
                    color="secondary"
                    variant="gradient"
                    onClick={() =>
                      handleOptionClick("location", "Esteso su più aree / diverse città")
                    }
                  >
                    Esteso su più aree / diverse città
                  </MDButton>
                </MDBox>
              </Card>
            </Grid>
          </Grid>
        );
      case 1:
        return (
          <Grid container spacing={3} justifyContent="center">
            <Grid item xs={12} lg={8}>
              <Card>
                <MDBox p={2}>
                  <MDTypography variant="h6">Scrivi Location/Città/Indirizzo *</MDTypography>
                  <MDTypography variant="body1">
                    In caso di evento esteso specifica più location o più città
                  </MDTypography>
                </MDBox>
                {formData.addresses.map((address, index) => (
                  <MDBox key={index} pt={2} px={2} pb={2}>
                    <Grid container spacing={3}>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          name="city"
                          label="Città*"
                          value={address.city}
                          onChange={(e) => handleAddressChange(index, "city", e.target.value)}
                          error={validationErrors.addresses[index]?.city || false}
                          helperText={validationErrors.addresses[index]?.city ? "Richiesto" : ""}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          name="province"
                          label="Provincia"
                          value={address.province}
                          onChange={(e) => handleAddressChange(index, "province", e.target.value)}
                          error={validationErrors.addresses[index]?.province || false}
                          helperText={
                            validationErrors.addresses[index]?.province ? "Richiesto" : ""
                          }
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          name="street"
                          label="Via*"
                          value={address.street}
                          onChange={(e) => handleAddressChange(index, "street", e.target.value)}
                          error={validationErrors.addresses[index]?.street || false}
                          helperText={validationErrors.addresses[index]?.street ? "Richiesto" : ""}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          name="houseNumber"
                          label="Civico"
                          value={address.houseNumber}
                          onChange={(e) =>
                            handleAddressChange(index, "houseNumber", e.target.value)
                          }
                          error={validationErrors.addresses[index]?.houseNumber || false}
                          helperText={
                            validationErrors.addresses[index]?.houseNumber ? "Richiesto" : ""
                          }
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          name="postalCode"
                          label="CAP"
                          value={address.postalCode}
                          onChange={(e) => handleAddressChange(index, "postalCode", e.target.value)}
                          error={validationErrors.addresses[index]?.postalCode || false}
                          helperText={
                            validationErrors.addresses[index]?.postalCode ? "Richiesto" : ""
                          }
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          name="locationName"
                          label="Nome Location"
                          value={address.locationName}
                          onChange={(e) =>
                            handleAddressChange(index, "locationName", e.target.value)
                          }
                          error={validationErrors.addresses[index]?.locationName || false}
                          helperText={
                            validationErrors.addresses[index]?.locationName ? "Richiesto" : ""
                          }
                        />
                      </Grid>
                    </Grid>
                  </MDBox>
                ))}
                {(formData.location === "Esteso su più aree / diverse città" ||
                  formData.location === "Una parte privata e una parte pubblica") && (
                  <MDBox mb={3} mx={2} display="flex" justifyContent="space-between" gap={1}>
                    {formData.addresses.length > 1 && (
                      <MDButton
                        fullWidth
                        color="secondary"
                        variant="gradient"
                        onClick={removeAddressField}
                      >
                        Rimuovi ultima città
                      </MDButton>
                    )}
                    <MDButton
                      fullWidth
                      color="secondary"
                      variant="gradient"
                      onClick={addAddressField}
                    >
                      Aggiungi un&apos;altra città
                    </MDButton>
                  </MDBox>
                )}
              </Card>
            </Grid>
          </Grid>
        );
      case 2:
        return (
          <Grid container spacing={3} justifyContent="center">
            <Grid item xs={12} lg={8}>
              <Card>
                <MDBox p={2}>
                  <MDTypography variant="h6">Imposta le Date dell&apos;Evento *</MDTypography>
                </MDBox>
                <MDBox pt={2} px={2} pb={2}>
                  {formData.addresses.map((address, index) => (
                    <Grid container spacing={2} mb={3} key={index}>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          label={`Data di inizio ${address.city}`}
                          type="date"
                          variant="outlined"
                          fullWidth
                          value={formData.startDates[index]}
                          onChange={(e) => handleDateChange(index, "startDates", e.target.value)}
                          InputLabelProps={{ shrink: true }}
                          error={validationErrors.startDates[index] || false}
                          helperText={validationErrors.startDates[index] ? "Richiesto" : ""}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          label={`Data di fine ${address.city}`}
                          type="date"
                          variant="outlined"
                          fullWidth
                          value={formData.endDates[index]}
                          onChange={(e) => handleDateChange(index, "endDates", e.target.value)}
                          InputLabelProps={{ shrink: true }}
                          error={validationErrors.endDates[index] || false}
                          helperText={validationErrors.endDates[index] ? "Richiesto" : ""}
                        />
                      </Grid>
                    </Grid>
                  ))}
                </MDBox>
              </Card>
            </Grid>
          </Grid>
        );
      case 3:
        return (
          <Grid container spacing={3} justifyContent="center">
            <Grid item xs={12} lg={8}>
              <Card>
                <MDBox p={2}>
                  <MDTypography variant="h6">
                    SELEZIONA LA TIPOLOGIA CHE DESCRIVE MEGLIO IL TUO EVENTO *
                  </MDTypography>
                  <MDBox pt={2} px={2} pb={2} display="flex" flexDirection="column" gap={1}>
                    {[
                      "CONCERTO / MUSICA DAL VIVO / DJ SET",
                      "MOSTRA / ESPOSIZIONE",
                      "RAPPRESENTAZIONE TEATRALE",
                      "MANIFESTAZIONE SPORTIVA",
                      "EVENTO PROMOZIONALE / TOUR PROMOZIONALE",
                      "EVENTO PRIVATO / EVENTO AZIENDALE",
                      "EVENTO CON ARTISTI DI STRADA / SFILATA / BANDA",
                      "FESTA DI STRADA",
                      "RIPRESE AUDIO, VIDEO E FOTO",
                      "NON SO",
                    ].map((type, index) => (
                      <MDButton
                        key={index}
                        variant={formData.eventType.includes(index + 1) ? "contained" : "outlined"}
                        color={formData.eventType.includes(index + 1) ? "success" : "secondary"}
                        onClick={() => handleMultiSelectClick("eventType", index + 1)}
                      >
                        {type}
                      </MDButton>
                    ))}
                  </MDBox>
                </MDBox>
              </Card>
            </Grid>
          </Grid>
        );
      case 4:
        return (
          <Grid container spacing={3} justifyContent="center">
            <Grid item xs={12} lg={8}>
              <Card>
                <MDBox p={2}>
                  <MDTypography variant="h6">DI QUALI PERMESSI HAI BISOGNO? *</MDTypography>
                  <MDBox pt={2} px={2} pb={2} display="flex" flexDirection="column" gap={1}>
                    {[
                      "OCCUPAZIONE SUOLO PUBBLICO",
                      "OCCUPAZIONE SUOLO PER CARICO SCARICO",
                      "PERMESSO SIAE",
                      "DEROGA IMPATTO ACUSTICO",
                      "LICENZA PUBBLICO SPETTACOLO",
                      "LICENZA DI SOMMINISTRAZIONE ALIMENTI/BEVANDE",
                      "VENDITA MERCHANDISING",
                      "LICENZA PUBBLICITARIA",
                      "LICENZA RIPRESE VIDEO/FOTO",
                      "LICENZA PER LAVORI NOTTURNI",
                      "PATROCINIO COMUNE/REGIONE/ENTI VARI",
                      "NESSUNO DI QUESTI/NON SO",
                      "PARTECIPAZIONE AL BANDO",
                      "MANIFESTAZIONE DI INTERESSE",
                    ].map((permission, index) => (
                      <MDButton
                        key={index}
                        variant={
                          formData.permissions.includes(index + 1) ? "contained" : "outlined"
                        }
                        color={formData.permissions.includes(index + 1) ? "success" : "secondary"}
                        onClick={() => handleMultiSelectClick("permissions", index + 1)}
                      >
                        {permission}
                      </MDButton>
                    ))}
                  </MDBox>
                </MDBox>
              </Card>
            </Grid>
          </Grid>
        );
      case 5:
        return (
          <Grid container spacing={3} justifyContent="center">
            <Grid item xs={12} lg={8}>
              <Card>
                <MDBox p={2}>
                  <MDTypography variant="h5">Inserisci il Nome dell&apos;Evento</MDTypography>
                </MDBox>
                <MDBox p={2}>
                  <TextField
                    label="Nome dell'Evento"
                    name="eventName"
                    value={formData.eventName}
                    onChange={handleInputChange}
                    error={validationErrors.eventName}
                    helperText={validationErrors.eventName ? "Questo campo è obbligatorio" : ""}
                    fullWidth
                    margin="normal"
                  />
                </MDBox>
              </Card>
            </Grid>
          </Grid>
        );
      case 6:
        return (
          <Grid container spacing={3} justifyContent="center">
            <Grid item xs={12} lg={8}>
              <Card>
                <MDBox p={2}>
                  <MDTypography variant="h6">VUOI AGGIUNGERE QUALCHE ALTRO DETTAGLIO?</MDTypography>
                  <MDBox pt={2} px={2} pb={2}>
                    <MDInput
                      fullWidth
                      multiline
                      rows={4}
                      value={formData.additionalDetails}
                      onChange={(e) =>
                        setFormData({ ...formData, additionalDetails: e.target.value })
                      }
                      placeholder="Inserisci ulteriori dettagli..."
                      variant="outlined"
                    />
                  </MDBox>
                </MDBox>
              </Card>
            </Grid>
          </Grid>
        );
      case 7:
        return (
          <Grid container spacing={3} justifyContent="center">
            <Grid item xs={12} lg={8}>
              <Card>
                <MDBox p={2}>
                  <MDTypography variant="h6">Inserisci i tuoi dati *</MDTypography>
                  <MDBox pt={2} px={2} pb={2} display="flex" flexDirection="column" gap={1}>
                    <MDInput
                      fullWidth
                      name="firstName"
                      label="Nome *"
                      value={formData.firstName}
                      onChange={handleInputChange}
                    />
                    <MDInput
                      fullWidth
                      name="lastName"
                      label="Cognome *"
                      value={formData.lastName}
                      onChange={handleInputChange}
                    />
                    <MDInput
                      fullWidth
                      name="phoneNumber"
                      label="Telefono *"
                      value={formData.phoneNumber}
                      onChange={handleInputChange}
                    />
                    <MDInput
                      fullWidth
                      name="email"
                      label="Email *"
                      value={formData.email}
                      onChange={handleInputChange}
                      error={validationErrors.email}
                    />
                    <MDInput
                      fullWidth
                      name="companyName"
                      label="Company"
                      value={formData.companyName}
                      onChange={handleInputChange}
                    />
                  </MDBox>
                </MDBox>
              </Card>
            </Grid>
          </Grid>
        );
      case 8:
        return (
          <Grid container spacing={3} justifyContent="center">
            <Grid item xs={12} lg={8}>
              <Card>
                <MDBox p={2}>
                  <InlineWidget
                    url="https://calendly.com/staff-permessiok/30min"
                    prefill={{
                      email: formData.email,
                      name: `${formData.firstName} ${formData.lastName}`.trim(),
                    }}
                  />
                </MDBox>
              </Card>
            </Grid>
          </Grid>
        );
      default:
        return "Unknown step";
    }
  };

  return (
    <PageLayout>
      <MDBox
        position="absolute"
        width="100%"
        minHeight="100vh"
        sx={{
          backgroundImage: ({ functions: { linearGradient, rgba }, palette: { gradients } }) =>
            linearGradient(
              rgba(gradients.dark.main, 0.6),
              rgba(gradients.dark.state, 0.6)
            ),
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      />
      <MDBox px={1} width="100%" height="100vh" mx="auto" position="relative" zIndex={2}>
        <MDBox py={3}>
          <MDBox mb={3} textAlign="center">
            <MDTypography variant="h3" fontWeight="bold" color="white" mb={1}>
              Richiesta Evento
            </MDTypography>
            <MDTypography variant="body1" color="white">
              Passo {step + 1} di {steps.length}: {steps[step]}
            </MDTypography>
          </MDBox>
          {renderStepContent()}
          <MDBox mt={3} display="flex" justifyContent="space-between" px={3}>
            {step > 0 && (
              <MDButton color="white" variant="gradient" onClick={handleBack}>
                Indietro
              </MDButton>
            )}
            {step < steps.length - 1 && (
              <MDButton color="info" variant="gradient" onClick={handleNext} sx={{ ml: "auto" }}>
                Avanti
              </MDButton>
            )}
          </MDBox>
        </MDBox>
        <Footer light />
      </MDBox>
      <MDSnackbar
        color={snackbar.color}
        icon={snackbar.icon}
        title={snackbar.title}
        content={snackbar.message}
        open={snackbar.open}
        onClose={closeSB}
        close={closeSB}
        bgWhite
      />
    </PageLayout>
  );
}

export default PublicEventRequest;
